/* global axios */
const knowledgeProxy = (accountId, path) =>
  `/api/v1/accounts/${accountId}/proxy/knowledge${path}`;

export const state = {
  query: {
    page: 1,
    page_size: 10,
    branch_id: undefined,
    product_id:  undefined,
    customer_id:  undefined,
    status:  undefined,
    start_date:  undefined,
    end_date: undefined,
  },
  trackingStatus: 'requested',
};

export const getters = {};

export const mutations = {
  SET_QUERY(state, query) {
    state.query = { ...state.query, ...query };
  },
  RESET_QUERY(state) {
    state.query = {
      page: 1,
      page_size: 10,
      branch_id: undefined,
      product_id: undefined,
      customer_id: undefined,
      status: state.trackingStatus,
      start_date: undefined,
      end_date: undefined,
      sort_by: 'date',
      sort_order: 'asc',
    };
  },
  setTrackingStatus(state, status) {
    state.trackingStatus = status;
  },
};

// `data` may be a FormData instance (e.g. product create/update) — axios
// detects that and lets the browser set the multipart boundary itself.
const request = async ({ accountId, method = 'GET', path, payload, params }) => {
  try {
    const { data } = await axios({
      method,
      url: knowledgeProxy(accountId, path),
      data: payload,
      params,
    });
    return data;
  } catch (err) {
    const response = err.response?.data;
    const messages =
      response?.detail?.map?.(detailErr => detailErr.msg).join('\n') ||
      response?.message ||
      err.message ||
      'Unexpected error occurred';
    throw new Error(messages);
  }
};

export const actions = {
  async createRentalBranch({ rootGetters }, { payload }) {
    const accountId = rootGetters.getCurrentAccountId;
    return await request({ accountId, method: 'POST', path: `/accounts/${accountId}/rental/branches`, payload });
  },

  async getAllRentalBranches({ rootGetters }, { page = 1, page_size = 10 }) {
    const accountId = rootGetters.getCurrentAccountId;
    return await request({ accountId, path: `/accounts/${accountId}/rental/branches`, params: { page, page_size } });
  },

  async getActiveRentalBranches({ rootGetters }, { page = 1, page_size = 10 }) {
    const accountId = rootGetters.getCurrentAccountId;
    return await request({ accountId, path: `/accounts/${accountId}/rental/active_branches`, params: { page, page_size } });
  },

  async getRentalBranchById({ rootGetters }, { branch_id }) {
    const accountId = rootGetters.getCurrentAccountId;
    return await request({ accountId, path: `/accounts/${accountId}/rental/branches/${branch_id}` });
  },

  async updateRentalBranch({ rootGetters }, { branch_id, payload }) {
    const accountId = rootGetters.getCurrentAccountId;
    return await request({ accountId, method: 'PUT', path: `/accounts/${accountId}/rental/branches/${branch_id}`, payload });
  },

  async deleteRentalBranch({ rootGetters }, { branch_id }) {
    const accountId = rootGetters.getCurrentAccountId;
    return await request({ accountId, method: 'DELETE', path: `/accounts/${accountId}/rental/branches/${branch_id}` });
  },

  // product endpoints
  async getAllProducts({ rootGetters }, { branch_id, page = 1, page_size = 10 }) {
    const accountId = rootGetters.getCurrentAccountId;
    return await request({ accountId, path: `/accounts/${accountId}/rental/products`, params: { branch_id, page, page_size } });
  },

  async createProduct({ rootGetters }, { payload }) {
    const accountId = rootGetters.getCurrentAccountId;
    return await request({ accountId, method: 'POST', path: `/accounts/${accountId}/rental/products`, payload });
  },

  async getActiveProducts({ rootGetters }, { branch_id, page = 1, page_size = 10 }) {
    const accountId = rootGetters.getCurrentAccountId;
    return await request({ accountId, path: `/accounts/${accountId}/rental/active_products`, params: { branch_id, page, page_size } });
  },

  async getProduct({ rootGetters }, { product_id }) {
    const accountId = rootGetters.getCurrentAccountId;
    return await request({ accountId, path: `/accounts/${accountId}/rental/products/${product_id}` });
  },

  async updateProduct({ rootGetters }, { product_id, payload}) {
    const accountId = rootGetters.getCurrentAccountId;
    return await request({ accountId, method: 'PUT', path: `/accounts/${accountId}/rental/products/${product_id}`, payload });
  },

  async deleteProduct({ rootGetters }, { product_id }) {
    const accountId = rootGetters.getCurrentAccountId;
    return await request({ accountId, method: 'DELETE', path: `/accounts/${accountId}/rental/products/${product_id}` });
  },

  // booking endpoints
  async createBooking({ rootGetters }, { payload }) {
    const accountId = rootGetters.getCurrentAccountId;
    return await request({ accountId, method: 'POST', path: `/accounts/${accountId}/rental/bookings`, payload });
  },

  async getBookings({ dispatch }) {
    return await dispatch('fetchBookings');
  },

  async fetchBookings({ rootGetters, state }) {
    const accountId = rootGetters.getCurrentAccountId;

    const params = {
      ...state.query,
      page: state.query.page ?? 1,
      page_size: state.query.page_size ?? 10,
      sort_by: state.query.sort_by ?? 'date',
      sort_order: state.query.sort_order ?? 'asc',
    };

    Object.keys(params).forEach(key => {
      if (params[key] === undefined || params[key] === null || params[key] === '') delete params[key];
    });

    return await request({ accountId, path: `/accounts/${accountId}/rental/bookings`, params });
  },

  updateFilter({ commit }, newQuery) {
    commit('SET_QUERY', newQuery);
  },

  resetFilter({ commit, dispatch }) {
    commit('RESET_QUERY');
    return dispatch('fetchBookings');
  },

  async deleteBooking({ rootGetters }, { booking_id }) {
    const accountId = rootGetters.getCurrentAccountId;
    return await request({ accountId, method: 'DELETE', path: `/accounts/${accountId}/rental/bookings/${booking_id}` });
  },

  async rescheduleBooking({ rootGetters }, { booking_id, payload }) {
    const accountId = rootGetters.getCurrentAccountId;
    return await request({ accountId, method: 'PUT', path: `/accounts/${accountId}/rental/bookings/${booking_id}/reschedule`, payload });
  },

  async updateLabel({ rootGetters }, { booking_id, payload }){
    const accountId = rootGetters.getCurrentAccountId;
    return await request({ accountId, method: 'PUT', path: `/accounts/${accountId}/rental/bookings/${booking_id}/label`, payload });
  },

  async updateStatus({ rootGetters }, { booking_id, payload }){
    const accountId = rootGetters.getCurrentAccountId;
    return await request({ accountId, method: 'PUT', path: `/accounts/${accountId}/rental/bookings/${booking_id}/status`, payload });
  },

  async updateNotes({ rootGetters }, { booking_id, payload }){
    const accountId = rootGetters.getCurrentAccountId;
    return await request({ accountId, method: 'PUT', path: `/accounts/${accountId}/rental/bookings/${booking_id}/notes`, payload });
  },

  async fetchAvailableDates({ rootGetters }, { query }) {
    const accountId = rootGetters.getCurrentAccountId;
    const params = {};
    Object.entries(query).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') params[key] = value;
    });

    return await request({ accountId, path: `/accounts/${accountId}/rental/available_dates`, params });
  },

  async checkRentalAvailability({ rootGetters }, { query }) {
    const accountId = rootGetters.getCurrentAccountId;
    const params = {};
    Object.entries(query).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') params[key] = value;
    });

    return await request({ accountId, path: `/accounts/${accountId}/rental/availability`, params });
  },

  updateTrackingStatus({commit, dispatch}, status){
    localStorage.setItem('trackRentalStatus', status);
    commit('setTrackingStatus', status);
    dispatch('fetchTrackingStatus');
  },

  fetchTrackingStatus({commit}){
    commit('setTrackingStatus', localStorage.getItem('trackRentalStatus') ? localStorage.getItem('trackRentalStatus') : 'upcoming');
  },

  // override endpoints
  async createOverride({ rootGetters }, { branch_id, payload }) {
    const accountId = rootGetters.getCurrentAccountId;
    return await request({ accountId, method: 'POST', path: `/accounts/${accountId}/rental/branches/${branch_id}/overrides`, payload });
  },

  async fetchOverrides({ rootGetters }, { branch_id, page = 1, page_size = 10 }) {
    const accountId = rootGetters.getCurrentAccountId;
    return await request({ accountId, path: `/accounts/${accountId}/rental/branches/${branch_id}/overrides`, params: { page, page_size } });
  },

  async deleteOverride({ rootGetters }, { branch_id, override_id }) {
    const accountId = rootGetters.getCurrentAccountId;
    return await request({ accountId, method: 'DELETE', path: `/accounts/${accountId}/rental/branches/${branch_id}/overrides/${override_id}` });
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
